// src/pages/CirclesPage.tsx
//
// CRM Circles list. Search + type filter + active/archived filter + owner
// filter, with type/visibility badges, member count, and click-through to
// the Circle detail page. Create is inline; edit/delete/archive live in
// the detail page (Settings tab). Realtime CIRCLE_* events invalidate the
// list cache via useCircleRealtime.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  useCreateCircleMutation,
  useGetCirclesQuery,
  useGetWorkspaceCentralTeamsQuery,
} from '../api/circlesApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useAuth } from '../hooks/useAuth';
import { useCircleOwnerCandidates } from '../components/circles/useCircleOwnerCandidates';
import { useCircleRealtime } from '../hooks/useCircleRealtime';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import CircleTypeBadge from '../components/circles/CircleTypeBadge';
import CircleVisibilityBadge from '../components/circles/CircleVisibilityBadge';
import CircleFormFields, {
  DEFAULT_CIRCLE_FORM_VALUES,
  type CircleFormValues,
} from '../components/circles/CircleFormFields';
import {
  CIRCLE_TYPE_LABELS,
} from '../components/circles/circleDisplay';
import { smartRuleErrorsFrom, smartRulesForSubmit } from '../components/circles/smartRules';
import type { Circle, CircleType, ContactFilterError, CreateCircleRequest } from '../types/api';
import { CIRCLE_TYPES } from '../types/api';

export default function CirclesPage() {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | CircleType>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useCircleRealtime({ workspaceId });

  const includeArchived = statusFilter !== 'active';
  const {
    data: circles = [],
    isLoading,
    error,
    refetch,
  } = useGetCirclesQuery(
    {
      workspaceId,
      q: searchQuery || undefined,
      type: typeFilter || undefined,
      includeArchived,
    },
    { skip: !workspaceId },
  );

  const visibleCircles = useMemo(() => {
    if (statusFilter === 'active') return circles.filter((c) => !c.archivedAt);
    if (statusFilter === 'archived') return circles.filter((c) => !!c.archivedAt);
    return circles;
  }, [circles, statusFilter]);

  const counts = useMemo(() => {
    const active = circles.filter((c) => !c.archivedAt).length;
    const archived = circles.filter((c) => !!c.archivedAt).length;
    return { active, archived, total: circles.length };
  }, [circles]);

  if (!workspaceId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view circles.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load circles." onRetry={refetch} />;
  }

  return (
    <div className="space-y-6" data-testid="circles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Circles</h1>
          <p className="text-sm text-gray-500">
            {counts.active} active · {counts.archived} archived
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="circles-new-btn">
          New Circle
        </Button>
      </div>

      {/* Filter bar */}
      <div
        className="flex flex-col sm:flex-row gap-3"
        role="search"
        aria-label="Filter circles"
      >
        <label className="relative flex-1">
          <span className="sr-only">Search circles</span>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search circles..."
            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="circles-search-input"
          />
          <svg
            aria-hidden
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </label>

        <label className="min-w-[180px]">
          <span className="sr-only">Filter by type</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as CircleType | '')}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="circles-type-filter"
          >
            <option value="">All types</option>
            {CIRCLE_TYPES.map((t) => (
              <option key={t} value={t}>
                {CIRCLE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>

        <label className="min-w-[140px]">
          <span className="sr-only">Filter by status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'active' | 'archived' | 'all')}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="circles-status-filter"
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
        </label>
      </div>

      {/* List */}
      {visibleCircles.length === 0 ? (
        <div
          className="text-center py-16 bg-white rounded-xl shadow-sm"
          data-testid="circles-empty-state"
        >
          {searchQuery || typeFilter ? (
            // A filtered-empty list is not an empty workspace.
            <>
              <p className="text-gray-500">No circles match your filters.</p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setTypeFilter('');
                }}
                className="mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                data-testid="circles-clear-filters"
              >
                Clear filters
              </button>
            </>
          ) : (
            <p className="text-gray-500">
              {statusFilter === 'archived'
                ? 'No archived circles.'
                : 'No circles yet. Create your first circle to group contacts.'}
            </p>
          )}
        </div>
      ) : (
        <ul
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="circles-list"
        >
          {visibleCircles.map((circle) => (
            <li key={circle.id}>
              <CircleListCard
                circle={circle}
                onOpen={() => navigate(`/circles/${circle.id}`)}
              />
            </li>
          ))}
        </ul>
      )}

      {showCreateModal && (
        <CreateCircleModal
          workspaceId={workspaceId}
          onClose={() => setShowCreateModal(false)}
          onCreated={(id) => {
            setShowCreateModal(false);
            navigate(`/circles/${id}`);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function CircleListCard({ circle, onOpen }: { circle: Circle; onOpen: () => void }) {
  // For department/team Circles, Central is the source of truth. Backend
  // sets `centralMemberCount` = number (authoritative) or null (Central
  // failure OR unlinked org Circle — either way "unknown"). We render
  // "—" for null; NEVER `.members?.length`, which would leak a
  // CRM-CircleMember-derived organizational count. For CRM-native Circle
  // types the field is absent → fall back to the local CircleMember count.
  //
  // Smart Circles: members are evaluated per viewer and never stored, so the
  // count is `smartMemberCount`: the matching contacts THIS viewer may see.
  const isCentralAuthoritative =
    (circle.type === 'department' || circle.type === 'team') &&
    circle.centralMemberCount !== undefined;
  const isSmart = circle.type === 'smart_segment';
  const memberCount = isSmart
    ? circle.smartMemberCount ?? null
    : isCentralAuthoritative
      ? circle.centralMemberCount ?? null
      : circle.members?.length ?? 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left bg-white rounded-xl shadow-sm p-4 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
      aria-label={`Open circle ${circle.name}`}
      data-testid={`circle-card-${circle.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {circle.color && (
              <span
                aria-hidden
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: circle.color }}
              />
            )}
            <h3 className="font-semibold text-gray-900 truncate">{circle.name}</h3>
          </div>
          {circle.description && (
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">
              {circle.description}
            </p>
          )}
        </div>
        <span
          className="text-xs whitespace-nowrap text-gray-500 bg-gray-100 px-2 py-1 rounded-full"
          data-testid={`circle-card-member-count-${circle.id}`}
        >
          {isSmart && circle.smartRulesActive === false
            ? 'No rules yet'
            : memberCount === null
              ? '— members'
              : `${memberCount} ${memberCount === 1 ? 'member' : 'members'}`}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <CircleTypeBadge type={circle.type} />
        <CircleVisibilityBadge visibility={circle.visibility} />
        {circle.archivedAt && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
            Archived
          </span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Create Modal
// ---------------------------------------------------------------------------

function CreateCircleModal({
  workspaceId,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [createCircle, { isLoading }] = useCreateCircleMutation();
  const { user } = useAuth();
  // Owner defaults to the creator — the same default the server applies.
  const [values, setValues] = useState<CircleFormValues>(() => ({
    ...DEFAULT_CIRCLE_FORM_VALUES,
    ownerUserId: user?.id ?? '',
  }));
  const owners = useCircleOwnerCandidates(workspaceId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ruleErrors, setRuleErrors] = useState<ContactFilterError[] | undefined>(undefined);
  // Phase 12 — the New Circle picker shows ONLY Teams where the caller
  // is an active Head. Non-Heads see zero options and cannot create a
  // Central-backed department/team Circle. Backend independently
  // rejects any Circle body that references a non-headed teamId.
  const { data: teamsData } = useGetWorkspaceCentralTeamsQuery({ onlyHeaded: true });
  // Parent candidates come from an UNFILTERED read: the page's list is
  // narrowed by its own search and type filters, which must not decide which
  // Circles can be a parent.
  const { data: allCircles = [] } = useGetCirclesQuery({ workspaceId });

  // Keyboard focus stays in the dialog, and returns to whatever opened it.
  //
  // The opener is captured during the FIRST RENDER (a state initializer), not
  // in an effect: the Name field's autoFocus runs at commit, before effects,
  // so an effect would record the Name input — which is then gone when focus
  // is "returned" to it.
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true);
  const [opener] = useState<HTMLElement | null>(() => document.activeElement as HTMLElement | null);
  useEffect(() => () => opener?.focus?.(), [opener]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setRuleErrors(undefined);
    if (!values.name.trim()) {
      setErrorMessage('Name is required.');
      return;
    }
    const smart = smartRulesForSubmit(values.type, values.smartRules);
    if (!smart.ok) {
      setRuleErrors(smart.errors);
      setErrorMessage('Finish the highlighted rules.');
      return;
    }
    // Phase 12.1 — Department/Team Circles MUST link a Central Team.
    // Backend re-verifies with 400 CENTRAL_TEAM_REQUIRED; this client
    // check gives immediate feedback and prevents the mutation call.
    if (
      (values.type === 'department' || values.type === 'team') &&
      !values.centralTeamId
    ) {
      setErrorMessage(
        'Choose the Central team. A Circle with Central team membership must be linked to a team you head.',
      );
      return;
    }
    const body: CreateCircleRequest = {
      workspaceId,
      name: values.name.trim(),
      description: values.description.trim() || null,
      type: values.type,
      icon: values.icon.trim() || null,
      color: values.color.trim() || null,
      visibility: values.visibility,
      ownerUserId: values.ownerUserId || null,
      parentCircleId: values.parentCircleId || null,
      centralTeamId: values.centralTeamId || null,
      ...(smart.rules ? { smartRules: smart.rules } : {}),
    };
    try {
      const created = await createCircle(body).unwrap();
      onCreated(created.id);
    } catch (err: any) {
      const fieldErrors = smartRuleErrorsFrom(err);
      if (fieldErrors) {
        setRuleErrors(fieldErrors);
        setErrorMessage('Some rules need attention.');
        return;
      }
      const message =
        err?.data?.error ||
        err?.data?.message ||
        'Failed to create circle. Please try again.';
      setErrorMessage(message);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLoading, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-circle-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
    >
      <div ref={dialogRef} className="bg-white rounded-xl shadow-xl ring-1 ring-slate-200 w-full max-w-md max-h-[85vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          {/* Header — compact */}
          <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
            <h2 id="create-circle-title" className="flex-1 text-sm font-semibold text-slate-900">
              Create Circle
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
            <CircleFormFields
              values={values}
              onChange={setValues}
              disabled={isLoading}
              candidateParents={allCircles}
              workspaceMembers={owners.members}
              ownerCandidatesStatus={owners.status}
              currentUserId={user?.id ?? null}
              centralTeams={teamsData?.teams}
              centralTeamsUnavailable={teamsData?.available === false}
              ruleErrors={ruleErrors}
            />
            {errorMessage && (
              <div
                role="alert"
                className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                {errorMessage}
              </div>
            )}
          </div>

          {/* Footer — compact */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !values.name.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              data-testid="circles-create-submit"
            >
              {isLoading && (
                <span className="h-3 w-3 rounded-full border-2 border-white/60 border-t-white animate-spin" aria-hidden />
              )}
              {isLoading ? 'Creating…' : 'Create Circle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
